/**
 * Credential Validation Utility
 *
 * Validates that all required external authentication credentials are provided.
 */

import { ExternalCredentials, CredentialValidationResult } from '../types';

/**
 * Validate that all required credential fields are present with non-empty values
 *
 * @param credentials - Provided credential values
 * @param requiredFields - List of required field names
 * @returns Validation result with valid flag and list of missing fields
 *
 * @example
 * ```typescript
 * const result = validateCredentialFields(
 *   { username: 'user', password: '' },
 *   ['username', 'password']
 * );
 * console.log(result); // { valid: false, missingFields: ['password'] }
 * ```
 */
export function validateCredentialFields(
  credentials: ExternalCredentials,
  requiredFields: string[]
): CredentialValidationResult {
  const missingFields: string[] = [];

  // Check each required field
  for (const field of requiredFields) {
    const value = credentials[field];

    // Field is missing if it's undefined, null, or empty string
    if (value === undefined || value === null || value.trim() === '') {
      missingFields.push(field);
    }
  }

  return {
    valid: missingFields.length === 0,
    missingFields,
  };
}
